import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, finalize } from 'rxjs/operators';
import { LoginService } from '../../service/login/login.service';
import { TokenService } from '../token.service';
import { AuthSessionService } from '../../service/disbursment/auth-session.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private sessionTimeout: any; // Timer reference for session timeout
  private hasShownPopup: any; // Flag to track if popup has been shown

  constructor(
    private authService: LoginService,
    private tokenService: TokenService,
    private authSessionService: AuthSessionService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // ✅ 1️⃣ Bypass global Bearer token for third-party APIs (would cause 401/403)
    if (
      request.url.includes('generativelanguage.googleapis.com') ||
      request.url.includes('map.api.dtn.com') ||
      request.url.includes('api.auth.dtn.com')
    ) {
      return next.handle(request);
    }

    if (this.authSessionService.isDisbursementMode()) {
        return next.handle(request);
      }
    // ✅ 2️⃣ Existing logic below stays exactly the same
    const authToken = localStorage.getItem('authToken') || '';
    const refreshToken = localStorage.getItem('authRefToken');

    if (request.url.includes('s3.amazonaws.com')) {
      return next.handle(request); // Skip adding auth header
    }

    const timeLeft = this.tokenService.getTimeUntilExpiration(authToken);

    if (timeLeft !== null && timeLeft <= 120000 && !this.hasShownPopup) {
      this.hasShownPopup = true; // Show popup only once!

      const userConfirmed = window.confirm(
        'Your session will expire in 2 minutes. Do you want to continue?'
      );

      if (userConfirmed) {
        return this.handleRefreshToken(request, next);
      } else {
        this.scheduleSessionLogout(timeLeft);
      }
    }

    // Attach Authorization header if token exists
    if (authToken) {
      request = request.clone({
        setHeaders: {
          Authorization: 'Bearer ' + authToken,
        },
      });
    } else {
      this.authService.logout();
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 403 &&
          error.error?.message === 'Session Time Out'
        ) {
          return this.handleRefreshToken(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (this.sessionTimeout) {
          clearTimeout(this.sessionTimeout);
          this.sessionTimeout = null;
        }
      })
    );
  }

  private handleRefreshToken(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const refreshToken = localStorage.getItem('authRefToken');

    if (refreshToken) {
      return from(
        this.authService.refreshToken({ refresh_token: refreshToken })
      ).pipe(
        switchMap((newToken: any) => {
          localStorage.setItem('authToken', newToken.access_token);
          localStorage.setItem('authRefToken', newToken.refresh_token);
          clearTimeout(this.sessionTimeout);
          this.sessionTimeout = null;
          this.hasShownPopup = false;

          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + newToken.access_token,
            },
          });

          return next.handle(clonedRequest);
        }),
        catchError((error) => {
          if (error.status === 403) {
            this.authService.logout();
          }
          return throwError(() => error);
        })
      );
    }

    this.authService.logout();
    return throwError(() => new Error('No refresh token available'));
  }

  private scheduleSessionLogout(timeLeft: number, userConfirmed?: any): void {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }
    this.sessionTimeout = setTimeout(() => {
      this.authService.logout();
      alert('Your session has expired. You have been logged out.');
    }, timeLeft);
  }
}
